// lib/utils/images.media.ts
// ✅ Production-safe URL normalizer for IndeMenu media
// Handles API responses like:
//   "http://api.indemenu.com/storage/vendors/2/logo/<id>" (no ext)
// Converts to the optimized public format:
//   "https://api.indemenu.com/storage/app/public/vendors/2/logo/<id>_md.webp"

const FALLBACK_IMAGES = {
  restaurant:
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070&auto=format&fit=crop",
  logo: "https://images.unsplash.com/photo-1573767291321-c0af2eaf5266?q=80&w=2070&auto=format&fit=crop",
} as const;

type ImageSize = "sm" | "md" | "lg";
type ImageType = "cover" | "logo";

function getApiBaseUrl(): string {
  const env =
    "https://api.indemenu.com";
  return env.replace(/\/+$/, "");
}

function hasOptimizedSuffix(pathname: string, size: ImageSize) {
  return pathname.includes(`_${size}.webp`);
}

function stripQueryAndHash(input: string) {
  return input.split("#")[0].split("?")[0];
}

function ensureLeadingSlash(pathname: string) {
  if (!pathname.startsWith("/")) return `/${pathname}`;
  return pathname;
}

function stripExtension(pathname: string) {
  // Only remove extension if there is one (e.g. .jpg/.png/.webp)
  // If no extension, keep as-is.
  return pathname.replace(/\.(webp|png|jpe?g|gif|bmp|tiff?)$/i, "");
}

function buildOptimizedPathFromPathname(
  pathnameRaw: string,
  size: ImageSize
): string {
  let pathname = ensureLeadingSlash(stripQueryAndHash(pathnameRaw));

  // If already optimized, keep it.
  if (pathname.includes("_sm.webp") || pathname.includes("_md.webp") || pathname.includes("_lg.webp")) {
    // normalize to https base, but keep path intact
    return pathname;
  }

  // Normalize server-provided public paths into your expected format
  // API returns: /storage/vendors/2/logo/<id>
  // Want:        /storage/app/public/vendors/2/logo/<id>_md.webp
  if (pathname.startsWith("/storage/vendors/")) {
    pathname = pathname.replace("/storage/vendors/", "/storage/app/public/vendors/");
  }

  // If it already starts with /storage/app/public/vendors/, keep it.
  if (pathname.startsWith("/storage/app/public/vendors/")) {
    const noExt = stripExtension(pathname);
    return `${noExt}_${size}.webp`;
  }

  // If it's already under /storage/ but not app/public, still try best-effort:
  // e.g. /storage/some/other/path -> /storage/app/public/some/other/path_size.webp
  if (pathname.startsWith("/storage/")) {
    const rest = pathname.replace(/^\/storage\//, "");
    const noExt = stripExtension(`/storage/app/public/${rest}`);
    return `${noExt}_${size}.webp`;
  }

  // Relative-ish paths like "vendors/2/logo/<id>" or "vendors/2/cover/<id>"
  // Normalize to /storage/app/public/<path>_size.webp
  const cleaned = pathname.replace(/^\/+/, "");
  const noExt = stripExtension(`/storage/app/public/${cleaned}`);
  return `${noExt}_${size}.webp`;
}

export function getOptimizedImageUrl(
  imagePath: string | null | undefined,
  size: ImageSize = "md",
  type: ImageType = "cover",
  options?: { fallback?: boolean }
): string {
  const fallbackEnabled = options?.fallback !== false;
  const baseUrl = getApiBaseUrl();

  if (!imagePath) {
    if (!fallbackEnabled) return "";
    return type === "logo" ? FALLBACK_IMAGES.logo : FALLBACK_IMAGES.restaurant;
  }

  const raw = stripQueryAndHash(String(imagePath).trim());
  if (!raw) {
    if (!fallbackEnabled) return "";
    return type === "logo" ? FALLBACK_IMAGES.logo : FALLBACK_IMAGES.restaurant;
  }

  try {
    // Full URL from API
    if (/^https?:\/\//i.test(raw)) {
      const u = new URL(raw);

      // Keep original pathname but force it through our optimizer
      const optimizedPath = buildOptimizedPathFromPathname(u.pathname, size);

      // If it came already optimized but on http, unify to API base (https)
      if (hasOptimizedSuffix(optimizedPath, size)) {
        return `${baseUrl}${optimizedPath}`;
      }

      // Normal optimized path
      return `${baseUrl}${optimizedPath}`;
    }

    // Non-URL input (relative or partial path)
    const optimizedPath = buildOptimizedPathFromPathname(raw, size);
    return `${baseUrl}${optimizedPath}`;
  } catch {
    if (!fallbackEnabled) return "";
    return type === "logo" ? FALLBACK_IMAGES.logo : FALLBACK_IMAGES.restaurant;
  }
}
